import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import useAgents from "../../hooks/useAgents";
import { investingApi, taskApi } from "../../services/api";
import { apiErrorMessage, parseApiError } from "../../services/apiError";
import { displayName } from "../tasks/userDisplay";
import { revealLater } from "./lib/reveal";
import useSubmitGuard from "../../hooks/useSubmitGuard";
import {
  myAgents,
  researchTaskDescription,
  researchTaskTitle,
} from "./lib/researchTask";

const MAIN = "h-11 md:h-9";

/** The agent picker and the question; it loads the agents only when shown. */
function RequestForm({
  symbol,
  me,
  busy,
  agentId,
  setAgentId,
  question,
  setQuestion,
  agentError,
  setAgentError,
  agentRef,
  onAvailable,
}) {
  const id = useId();
  const { agents, loading, error, refresh } = useAgents();
  const mine = myAgents(agents, me);
  const only = mine.length === 1 ? mine[0].id : null;

  // One agent: it's the choice.
  useEffect(() => {
    if (only !== null && !agentId) setAgentId(String(only));
  }, [only, agentId, setAgentId]);
  // How many there are to pick from (null while unknown): none, no create.
  const available = loading || error ? null : mine.length;
  useEffect(() => {
    onAvailable(available);
  }, [available, onAvailable]);

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-agent`}>Agent</Label>
        <NativeSelect
          ref={agentRef}
          id={`${id}-agent`}
          value={agentId}
          className={MAIN}
          disabled={Boolean(busy)}
          aria-invalid={agentError ? true : undefined}
          aria-describedby={`${id}-agent-help${agentError ? ` ${id}-agent-error` : ""}`}
          onChange={(event) => {
            setAgentId(event.target.value);
            setAgentError(null);
          }}
        >
          <option value="">{loading ? "Loading your agents…" : "Choose an agent"}</option>
          {mine.map((agent) => (
            <option key={agent.id} value={String(agent.id)}>
              {displayName(agent)}
            </option>
          ))}
        </NativeSelect>
        {agentError && (
          <p id={`${id}-agent-error`} className="m-0 text-sm text-destructive">
            {agentError}
          </p>
        )}
        <p id={`${id}-agent-help`} className="m-0 text-xs text-muted-foreground">
          Only your own agents: the research an agent records is filed under its person.
        </p>
        {!loading && !error && mine.length === 0 && (
          <p className="m-0 text-sm">
            You have no active agents. Mint one under{" "}
            <Link to="/agents">Agents</Link>, then come back.
          </p>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>Couldn&apos;t list your agents: {apiErrorMessage(error, "Something went wrong.")}</span>{" "}
              <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={() => refresh()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-question`}>Question (optional)</Label>
        <Textarea
          id={`${id}-question`}
          value={question}
          placeholder="e.g. Is the data-centre lead durable once the hyperscalers' own chips ship?"
          className="max-h-48 min-h-20"
          aria-describedby={`${id}-question-help`}
          disabled={Boolean(busy)}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <p id={`${id}-question-help`} className="m-0 text-xs text-muted-foreground">
          Goes in the task&apos;s description, for the agent to answer. Leave it empty for the
          standard research on {symbol}.
        </p>
      </div>
    </>
  );
}

RequestForm.propTypes = {
  symbol: PropTypes.string.isRequired,
  me: PropTypes.shape({ id: PropTypes.number }),
  busy: PropTypes.string,
  agentId: PropTypes.string.isRequired,
  setAgentId: PropTypes.func.isRequired,
  question: PropTypes.string.isRequired,
  setQuestion: PropTypes.func.isRequired,
  agentError: PropTypes.string,
  setAgentError: PropTypes.func.isRequired,
  agentRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  onAvailable: PropTypes.func.isRequired,
};

/**
 * "Research this" (spec walkthrough 2, D3): pick one of your agents and
 * add a question if you like. It creates the board task "Investing: research
 * <SYMBOL>" through the existing tasks API, assigned to that agent, with the
 * question and a link to the company's page in its description — then links
 * the task to the company (PATCH research_task), and the page shows
 * "Research requested — task #N".
 *
 * Two requests, so two ways to fail, each said as it is:
 * - the create refused: nothing was made, try again; the create unanswered:
 *   it may have landed, check the board first;
 * - the link failed after the task was made: the task stays on the board,
 *   and the dialog offers to link THAT task again — never a second one.
 *   `onTaskCreated(symbol, task)` hands the task to the page the moment it
 *   exists; the section keeps it (useUnlinked: by symbol, for this tab,
 *   across pages and reloads), so the dialog reopens on "Link task #N"
 *   (`pendingTask`), not on a fresh create. A new tab doesn't know it.
 *
 * `onLinked(symbol, company, task)` gets the company's page as the PATCH
 * returned it. Static backdrop; Escape, Cancel and closing are refused
 * while a request is in flight.
 */
function ResearchThisDialog({
  open,
  company,
  me,
  pendingTask = null,
  onOpenChange,
  onTaskCreated,
  onLinked,
  onForgetTask,
  onCloseAutoFocus,
}) {
  const symbol = company?.symbol ?? "";
  const [busy, setBusy] = useState(null); // "create" | "link"
  const [agentId, setAgentId] = useState("");
  const [question, setQuestion] = useState("");
  const [agentError, setAgentError] = useState(null);
  const [error, setError] = useState(null); // {text, unknown?}
  // The task this dialog made, or the page's unlinked one: link only.
  const [task, setTask] = useState(pendingTask);
  const [available, setAvailable] = useState(null);
  const agentRef = useRef(null);
  const linkRef = useRef(null);
  const errorRef = useRef(null);
  const submitGuard = useSubmitGuard(open);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Each opening starts from the page's view of this company — set while
  // rendering the opening, not in an effect: Radix picks the field to focus
  // in its own mount effect, which runs before ours, so an effect would
  // leave the last request's task on screen with nothing focused.
  const opening = open ? symbol : null;
  const [openedFor, setOpenedFor] = useState(null);
  if (opening !== openedFor) {
    setOpenedFor(opening);
    if (opening !== null) {
      setTask(pendingTask);
      setBusy(null);
      setError(null);
      setAgentError(null);
      setAgentId("");
      setQuestion("");
    }
  }

  // Cancel is disabled and Escape refused while a request is in flight,
  // and nothing else closes it (static backdrop, no X).
  const close = () => onOpenChange(false);

  const link = async (made) => {
    setBusy("link");
    setError(null);
    try {
      const { data } = await investingApi.companies.patch(symbol, { research_task: made.id });
      onLinked(symbol, data, made);
      if (mounted.current) onOpenChange(false);
    } catch (err) {
      if (!mounted.current) return;
      setError({
        text: `Task #${made.id} is on the board, but linking it to ${symbol} failed: ${apiErrorMessage(err, "Something went wrong.")}`,
      });
      setTimeout(() => linkRef.current?.focus(), 0);
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const create = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!agentId) {
      setAgentError("Choose the agent who does the research.");
      agentRef.current?.focus();
      return;
    }
    setBusy("create");
    setError(null);
    let made;
    try {
      const { data } = await taskApi.create({
        title: researchTaskTitle(symbol),
        description: researchTaskDescription({
          symbol,
          name: company?.name,
          question,
          origin: window.location.origin,
        }),
        assignee_id: Number(agentId),
      });
      made = data;
    } catch (err) {
      if (!mounted.current) return;
      setBusy(null);
      // An answer from the server means it refused: nothing was made. No
      // answer (offline, a timeout) means it may have landed.
      if (!err?.response) {
        setError({
          text: `No answer from the server (${apiErrorMessage(err, "network error")}), so the task may have been created.`,
          unknown: true,
        });
        revealLater(errorRef);
        return;
      }
      const { formError, fieldErrors } = parseApiError(err, "Couldn't create the task.");
      // The agent refused (gone, moved, deactivated since the list loaded):
      // on the Agent field, in a person's words, not "assignee_id: …".
      const { assignee_id: agentRefused, ...otherFields } = fieldErrors;
      const rest = [formError, ...Object.entries(otherFields).map(([name, value]) => `${name}: ${[].concat(value).join(", ")}`)]
        .filter(Boolean)
        .join(" — ");
      if (agentRefused) {
        setAgentError("That agent can't take this task — pick another, or refresh the list. No task was created.");
      }
      if (rest) {
        setError({ text: `${rest} No task was created — fix that and try again.` });
      }
      if (agentRefused) setTimeout(() => agentRef.current?.focus(), 0);
      else revealLater(errorRef);
      return;
    }
    onTaskCreated(symbol, made);
    if (!mounted.current) return;
    setTask(made);
    await link(made);
  };

  const forget = () => {
    onForgetTask?.(symbol);
    setTask(null);
    setError(null);
    setTimeout(() => agentRef.current?.focus(), 0);
  };

  const linking = Boolean(task);
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      {/* data-investing: portalled out of the layout's wrapper, so it
          carries the scope that keeps its muted text AA (tailwind.css). */}
      <DialogContent
        data-investing=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (pendingTask ? linkRef : agentRef).current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">Research {symbol}</DialogTitle>
          <DialogDescription>
            Creates the board task &ldquo;{researchTaskTitle(symbol)}&rdquo; for the agent you
            pick, then links it to {symbol} here. The agent&apos;s note — its scores, its evidence
            and any verdict it proposes — lands on {symbol}&apos;s page; the verdict stays yours.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={linking ? (e) => { e.preventDefault(); if (!busy) link(task); } : create} noValidate className="flex min-h-0 flex-1 flex-col">
          <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            {linking ? (
              !error && (
                <p className="m-0">
                  Task #{task.id} is on the board
                  {task.assignee?.username ? `, assigned to ${task.assignee.username},` : ""} but
                  isn&apos;t linked to {symbol} yet. Linking it shows &ldquo;Research requested —
                  task #{task.id}&rdquo; here until the agent closes it.
                </p>
              )
            ) : (
              <RequestForm
                symbol={symbol}
                me={me}
                busy={busy}
                agentId={agentId}
                setAgentId={setAgentId}
                question={question}
                setQuestion={setQuestion}
                agentError={agentError}
                setAgentError={setAgentError}
                agentRef={agentRef}
                onAvailable={setAvailable}
              />
            )}
            {error && (
              <Alert ref={errorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription>
                  {error.text}
                  {error.unknown && (
                    <>
                      {" "}
                      Check <Link to="/tasks/board">the board</Link> for &ldquo;
                      {researchTaskTitle(symbol)}&rdquo; before trying again.
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {linking && !busy && (
              <p className="m-0 text-xs text-muted-foreground">
                Task #{task.id} gone from the board?{" "}
                <button
                  type="button"
                  className="text-primary underline underline-offset-2"
                  onClick={forget}
                >
                  Start over with a new task
                </button>
              </p>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" className={MAIN} disabled={Boolean(busy)} onClick={close}>
              {linking ? "Close" : "Cancel"}
            </Button>
            <Button
              ref={linkRef}
              type="submit"
              className={MAIN}
              disabled={Boolean(busy) || (!linking && available === 0)}
              {...submitGuard}
            >
              {busy && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy === "create"
                ? "Creating the task…"
                : busy === "link"
                  ? `Linking task #${task?.id}…`
                  : linking
                    ? error
                      ? `Link task #${task.id} again`
                      : `Link task #${task.id}`
                    : "Create the task and link it"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

ResearchThisDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The company: `{symbol, name}`. */
  company: PropTypes.shape({ symbol: PropTypes.string.isRequired, name: PropTypes.string }),
  /** The signed-in person (their agents are the choices). */
  me: PropTypes.shape({ id: PropTypes.number }),
  /** A task made earlier for this company and not linked: link it, don't make another. */
  pendingTask: PropTypes.shape({
    id: PropTypes.number.isRequired,
    assignee: PropTypes.shape({ username: PropTypes.string }),
  }),
  onOpenChange: PropTypes.func.isRequired,
  /** `(symbol, task)`: the task exists (before the link is tried). */
  onTaskCreated: PropTypes.func.isRequired,
  /** `(symbol, company, task)`: linked; `company` is the PATCH's page. */
  onLinked: PropTypes.func.isRequired,
  /** `(symbol)`: drop the unlinked task and start over. */
  onForgetTask: PropTypes.func,
  onCloseAutoFocus: PropTypes.func,
};

export default ResearchThisDialog;
